import React from 'react';
import { withRouter } from 'react-router-dom';

import Api from '../api';
import Util from '../util';
import resolvePortalSettings from './index';

const PortalSettingsContext = React.createContext({
  portal: resolvePortalSettings({}, {}),
  application: null
});

// Fetches the application, then resolves the master portal settings from the
// application's `portal` block and the current query string. Children are not
// rendered until the fetch settles so a toggle never flashes on and then off.
// A failed or missing fetch resolves from the query alone, which is today's behavior.
// Settings are recomputed on navigation, and the application is refetched when
// `client_id` changes.
class PortalSettingsProvider extends React.Component {

  constructor(props) {

    super(props);

    this.state = {
      application: null,
      applicationClientId: null,
      ready: !this.getClientId()
    };
  }

  componentDidMount() {
    this.fetchApplication();
  }

  componentDidUpdate() {

    // A new client_id (or none) invalidates the application we hold and any request
    // still in flight, so compare against what was last requested, not what is held.
    if (this.getClientId() !== this.fetchedFor) {
      this.fetchApplication();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  getQuery() {
    return Util.queryToObject((this.props.location || window.location).search);
  }

  // A scalar key for the application: a repeated `client_id` parses to an array
  // (a new one on every call), so the first value is used and an absent one is null.
  getClientId() {

    const { client_id: clientId } = this.getQuery();

    return (Array.isArray(clientId) ? clientId[0] : clientId) || null;
  }

  async fetchApplication() {

    const clientId = this.getClientId();
    this.fetchedFor = clientId;

    // Only the latest request may update state, even when an earlier one was for the same id.
    const requestId = this.requestId = (this.requestId || 0) + 1;

    if (!clientId) {
      this.setState({ application: null, applicationClientId: null, ready: true });
      return;
    }

    this.setState({ application: null, applicationClientId: null, ready: false });

    let application = null;

    try {
      const res = await new Api().get(`/application/${clientId}`);

      if (res && res.code === 200) {
        application = res.content;
      }
    }
    catch (e) {
      // Fall through to query-only settings.
    }

    // Ignore a response superseded by a newer request (including one for the same id).
    if (this.unmounted || this.requestId !== requestId) {
      return;
    }

    this.setState({ application, applicationClientId: clientId, ready: true });
  }

  render() {

    // Hold back children until the application belongs to the current client_id;
    // componentDidUpdate only starts the refetch after this render.
    if (!this.state.ready || this.state.applicationClientId !== this.getClientId()) {
      return null;
    }

    const { application } = this.state;
    const value = {
      application,
      portal: resolvePortalSettings(application && application.portal, this.getQuery())
    };

    return (
      <PortalSettingsContext.Provider value={value}>
        {this.props.children}
      </PortalSettingsContext.Provider>
    );
  }
}

// Injects `portal` (the resolved master settings) into a component's props.
const withPortalSettings = (Component) => {

  const Wrapped = (props) => (
    <PortalSettingsContext.Consumer>
      {({ portal, application }) => <Component {...props} portal={portal} portalApplication={application} />}
    </PortalSettingsContext.Consumer>
  );

  Wrapped.displayName = `withPortalSettings(${Component.displayName || Component.name || 'Component'})`;

  return Wrapped;
};

const RoutedPortalSettingsProvider = withRouter(PortalSettingsProvider);

export { PortalSettingsContext, RoutedPortalSettingsProvider as PortalSettingsProvider, withPortalSettings };
